import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Siren, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "../../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { QuestionInput } from "../../components/joe/QuestionInput";
import { JoeHelper } from "../../components/joe/JoeHelper";
import { ConflictCard } from "../../components/joe/ConflictCard";
import { Logo, UrgencyBadge } from "../../components/joe/Badges";
import { api, errMsg } from "../../lib/api";
import { SECTIONS, visibleQuestions, flatAnswers } from "../../lib/questions";
import { Guide, SECTION_GUIDE } from "../../lib/brand";

const isEmpty = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length) || (typeof v === "object" && !Array.isArray(v) && !Object.keys(v).length);

export const ScamBanner = () => (
  <div data-testid="scam-warning-banner" className="rounded-2xl border-2 border-red-600 bg-red-600 p-4 text-white">
    <p className="flex items-center gap-2 font-display font-extrabold uppercase tracking-wide"><ShieldAlert className="h-5 w-5" /> Potential consumer-protection issue detected</p>
    <p className="mt-1 text-sm text-red-50">Please get independent professional review before signing anything, transferring your deed, or sending money.</p>
  </div>
);

export default function Interview() {
  const nav = useNavigate();
  const [answers, setAnswers] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [urgentOpen, setUrgentOpen] = useState(false);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const draft = useRef({ val: null, conf: "reported" });

  useEffect(() => {
    api.get("/case").then(({ data }) => {
      setAnswers(data.answers);
      setAnalysis(data.analysis);
      const vis = visibleQuestions(flatAnswers(data.answers));
      let i = vis.findIndex((q) => q.key === data.current_key);
      if (i < 0) i = vis.findIndex((q) => !(q.key in data.answers));
      setIdx(i < 0 ? vis.length - 1 : i);
    }).catch(() => nav("/login"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const A = useMemo(() => flatAnswers(answers), [answers]);
  const vis = useMemo(() => visibleQuestions(A), [A]);
  if (!answers) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  const q = vis[Math.min(idx, vis.length - 1)];
  const pct = Math.round((100 * idx) / vis.length);

  const save = async (skip) => {
    const { val, conf } = draft.current;
    if (!skip && isEmpty(val) && conf !== "unknown") return toast.error("Please choose an answer, or tap “Skip for now”.");
    setSaving(true);
    const value = skip || conf === "unknown" ? null : val;
    const confidence = skip ? "unknown" : conf;
    const nextAnswers = { ...answers, [q.key]: { value, confidence } };
    const nextVis = visibleQuestions(flatAnswers(nextAnswers));
    const pos = nextVis.findIndex((x) => x.key === q.key);
    const nextQ = nextVis[pos + 1];
    try {
      const { data } = await api.put("/case/answer", { key: q.key, value, confidence, progress: Math.round((100 * (pos + 1)) / nextVis.length), next_key: nextQ?.key || null });
      const prevOpen = analysis?.open_conflicts || 0;
      setAnswers(nextAnswers);
      setAnalysis(data.analysis);
      if (q.s === "triage" && ["RED", "ORANGE"].includes(data.analysis.urgency.color) && !nextQ?.key?.startsWith("deadline")) setUrgentOpen(true);
      if (data.analysis.open_conflicts > prevOpen) setConflictsOpen(true);
      if (!nextQ) {
        await api.post("/case/complete");
        toast.success("Your situation has been organized.");
        return nav("/case?done=1");
      }
      setIdx(pos + 1);
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const back = (why) => {
    if (why === "wrong") toast("No problem — correct the previous answer below.");
    setIdx(Math.max(0, idx - 1));
  };

  return (
    <div className="grain min-h-screen">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-3">
          <Logo />
          <button data-testid="interview-case-link" onClick={() => nav("/case")} className="text-sm font-semibold text-slate-600 hover:text-slate-900">My case</button>
        </div>
        <div className="mx-auto max-w-3xl px-5 pb-3">
          <div className="mb-1 flex justify-between font-mono text-[11px] uppercase tracking-widest text-slate-500">
            <span data-testid="interview-section">{SECTIONS[q.s]}</span><span data-testid="interview-progress">{pct}% complete</span>
          </div>
          <Progress value={pct} className="h-1.5" />
        </div>
      </header>
      <main className="relative z-10 mx-auto max-w-3xl px-5 pb-24 pt-8">
        {analysis?.scam?.detected && <div className="mb-6"><ScamBanner /></div>}
        {analysis && analysis.urgency.color !== "GREEN" && (
          <div className="mb-6 flex flex-wrap items-center gap-3"><UrgencyBadge color={analysis.urgency.color} label={analysis.urgency.label} />
            {analysis.urgency.days_remaining !== null && <span className="font-mono text-xs text-slate-600" data-testid="days-remaining">{analysis.urgency.days_remaining} days remaining</span>}
            <span className="text-xs text-slate-500">Preliminary — not a legal conclusion.</span>
          </div>
        )}
        <div key={q.key} className="rise">
          <div className="mb-4 flex items-center justify-between gap-3"><Guide who={SECTION_GUIDE[q.s] || "joe"} testId="question-guide" />{SECTION_GUIDE[q.s] !== "joe" && <Guide who="joe" size={34} label={false} testId="question-guide-joe" />}</div>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "-0.01em" }} className="text-2xl font-bold leading-snug text-brand-navy sm:text-3xl" data-testid="question-text">{q.q}</h1>
          <p className="mt-2 text-sm text-slate-500" data-testid="question-why">Why Joe asks: {q.why}</p>
          <div className="mt-6"><QuestionInput q={q} initial={answers[q.key]} onChange={(val, conf) => { draft.current = { val, conf }; }} /></div>
        </div>
        <div className="mt-8 flex items-center justify-between gap-3">
          <button data-testid="question-back-btn" disabled={idx === 0} onClick={() => back()} className="tap inline-flex h-12 items-center gap-2 rounded-xl px-4 font-semibold text-slate-600 disabled:opacity-30"><ArrowLeft className="h-4 w-4" /> Back</button>
          <div className="flex gap-2">
            <button data-testid="question-skip-btn" onClick={() => save(true)} disabled={saving} className="tap h-12 rounded-xl px-4 text-sm font-semibold text-slate-500 hover:text-slate-900">Skip for now</button>
            <button data-testid="question-next-btn" onClick={() => save(false)} disabled={saving} className="tap inline-flex h-12 items-center gap-2 rounded-xl bg-slate-900 px-6 font-display font-bold uppercase tracking-wide text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Next <ArrowRight className="h-4 w-4" /></>}
            </button>
          </div>
        </div>
        <div className="mt-10 border-t border-slate-200 pt-6"><JoeHelper key={q.key} q={q} onBack={back} /></div>
      </main>
      <Dialog open={urgentOpen} onOpenChange={setUrgentOpen}>
        <DialogContent data-testid="urgent-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-xl text-red-700"><Siren className="h-6 w-6" /> Your deadline may be close</DialogTitle></DialogHeader>
          <p className="text-slate-700">Based on the date you entered ({analysis?.urgency.days_remaining ?? "unknown"} days remaining), the most important step is getting a specialist or qualified attorney involved quickly. You can finish the rest of the questions later.</p>
          <div className="grid gap-2">
            <button data-testid="urgent-talk-specialist-btn" onClick={() => nav("/case?tab=specialist")} className="tap h-12 rounded-xl bg-red-600 font-display font-bold uppercase text-white">Talk to a specialist now</button>
            <button data-testid="urgent-upload-btn" onClick={() => nav("/case?tab=documents")} className="tap h-12 rounded-xl border-2 border-slate-900 font-bold uppercase">Upload my notice</button>
            <button data-testid="urgent-continue-btn" onClick={() => setUrgentOpen(false)} className="h-10 text-sm font-semibold text-slate-500">Continue questions</button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={conflictsOpen} onOpenChange={setConflictsOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="conflicts-dialog">
          <DialogHeader><DialogTitle className="font-display">Joe noticed something</DialogTitle></DialogHeader>
          {analysis?.conflicts.filter((c) => !c.resolved).map((c) => (
            <ConflictCard key={c.id} c={c} onResolved={(an) => { setAnalysis(an); if (!an.open_conflicts) setConflictsOpen(false); api.get("/case").then(({ data }) => setAnswers(data.answers)); }} />
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
